

import { CheckCircle, Download, ArrowRight, User } from 'lucide-react';
 
function downloadReceipt({ paymentId, plan, amount, userName }) {
  const date = new Date().toLocaleString('en-IN', { dateStyle: 'long', timeStyle: 'short' });
  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>EasyChat Receipt – ${paymentId}</title>
  <style>
    body { font-family: system-ui, sans-serif; background: #f9fafb; margin: 0; padding: 40px; color: #111; }
    .card { background: #fff; max-width: 480px; margin: 0 auto; border-radius: 16px; padding: 40px; box-shadow: 0 4px 24px rgba(0,0,0,0.08); }
    .logo { font-size: 22px; font-weight: 900; color: #111; margin-bottom: 4px; }
    .logo span { color: #82bbe0; }
    .badge { display: inline-block; background: #e8f5ed; color: #16a34a; font-size: 11px; font-weight: 700; padding: 3px 10px; border-radius: 20px; margin-bottom: 24px; }
    h2 { font-size: 20px; margin: 0 0 6px; }
    .sub { color: #6b7280; font-size: 13px; margin-bottom: 28px; }
    .divider { border: none; border-top: 1px solid #f0f0f0; margin: 20px 0; }
    .row { display: flex; justify-content: space-between; font-size: 13px; margin-bottom: 10px; }
    .row .label { color: #6b7280; }
    .row .value { font-weight: 600; }
    .row .mono { font-family: monospace; font-size: 11px; color: #82bbe0; }
    .total { font-size: 15px; font-weight: 800; }
    .footer { margin-top: 28px; font-size: 11px; color: #9ca3af; text-align: center; }
  </style>
</head>
<body>
  <div class="card">
    <div class="logo">Easy<span>Chat</span></div>
    <p style="font-size:11px;color:#9ca3af;margin:0 0 16px">Your Conversations, Simplified</p>
    <span class="badge">✓ Payment Confirmed</span>
    <h2>Payment Receipt</h2>
    <p class="sub">Thank you for your purchase. Your plan is now active.</p>
    <hr class="divider"/>
    <div class="row"><span class="label">Customer</span><span class="value">${userName}</span></div>
    <div class="row"><span class="label">Date</span><span class="value">${date}</span></div>
    <div class="row"><span class="label">Payment ID</span><span class="mono">${paymentId}</span></div>
    <div class="row"><span class="label">Plan</span><span class="value">${plan?.name}</span></div>
    <div class="row"><span class="label">Category</span><span class="value">${plan?.category}</span></div>
    <hr class="divider"/>
    <div class="row total"><span>Total Paid</span><span>₹${amount?.toLocaleString('en-IN')}</span></div>
    <div class="row"><span class="label">Status</span><span style="color:#16a34a;font-weight:700">Confirmed</span></div>
    <hr class="divider"/>
    <div class="footer">
      Secured by Razorpay · 256-bit SSL encryption<br/>
      © ${new Date().getFullYear()} EasyChat. All rights reserved.
    </div>
  </div>
</body>
</html>`;
 
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `easychat-receipt-${paymentId}.html`;
  a.click();
  URL.revokeObjectURL(url);
}
 
export default function PaymentSuccess({ paymentId, plan, amount, userName, onDone }) {
  return (
    <div className="w-full min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="bg-white rounded-3xl shadow-2xl p-10 w-full max-w-lg mx-auto text-center my-12">
        <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={44} className="text-green-500" strokeWidth={1.5} />
        </div>
 
        <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Payment Successful!</h2>
        <p className="text-gray-500 text-sm mb-6">
          Your payment has been processed successfully. Your plan is now active.
        </p>
 
        {/* Purchased Plan Banner */}
        <div className="bg-[#82bbe0]/10 border border-[#82bbe0]/30 rounded-2xl p-4 mb-5 text-left">
          <div className="flex items-center gap-2 mb-2">
            <User size={14} className="text-[#82bbe0]" />
            <span className="text-xs font-semibold text-[#82bbe0] uppercase tracking-wide">Purchased Plan</span>
          </div>
          <p className="text-base font-bold text-gray-900">{plan?.name}</p>
          <p className="text-sm text-gray-500 mt-0.5">Activated for <span className="font-semibold text-gray-700">{userName}</span></p>
        </div>
 
        <div className="bg-gray-50 rounded-2xl p-5 mb-6 text-left space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Customer</span>
            <span className="font-semibold text-gray-900">{userName}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Plan</span>
            <span className="font-semibold text-gray-900">{plan?.name}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Amount Paid</span>
            <span className="font-semibold text-gray-900">₹{amount?.toLocaleString('en-IN')}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Payment ID</span>
            <span className="font-mono text-xs text-[#82bbe0] font-semibold">{paymentId}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Status</span>
            <span className="text-green-600 font-semibold">Confirmed</span>
          </div>
        </div>
 
        <div className="flex gap-3">
          <button
            onClick={() => downloadReceipt({ paymentId, plan, amount, userName })}
            className="flex-1 flex items-center justify-center gap-2 py-3 border-2 border-gray-100 rounded-xl text-sm font-semibold text-gray-600 hover:border-[#82bbe0] hover:text-[#82bbe0] transition-all"
          >
            <Download size={15} /> Invoice
          </button>
          <button
            onClick={onDone}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#82bbe0] rounded-xl text-sm font-semibold text-white hover:bg-[#6aabce] transition-all shadow-lg shadow-[#82bbe0]/30"
          >
            Continue <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}